import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { App as CapacitorApp } from "@capacitor/app";
import { isNativePlatform } from "@/lib/platform";
import { cn } from "@/lib/utils";

interface FullscreenImageViewerProps {
  open: boolean;
  onClose: () => void;
  images: string[];
  initialIndex?: number;
  alt?: string;
}

export const FullscreenImageViewer = ({
  open,
  onClose,
  images,
  initialIndex = 0,
  alt = "Generated design",
}: FullscreenImageViewerProps) => {
  const [index, setIndex] = useState(initialIndex);
  const hasMultiple = images.length > 1;

  useEffect(() => {
    if (open) setIndex(Math.min(initialIndex, Math.max(images.length - 1, 0)));
  }, [open, initialIndex, images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" && hasMultiple) setIndex((i) => (i + 1) % images.length);
      else if (e.key === "ArrowLeft" && hasMultiple) setIndex((i) => (i - 1 + images.length) % images.length);
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, hasMultiple, images.length, onClose]);

  // Back-button / history handling (mirrors ExploreDesignModal pattern)
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const consumedSentinelRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    consumedSentinelRef.current = false;
    window.history.pushState({ __fullscreenViewer: true }, "");

    const onPop = () => {
      consumedSentinelRef.current = true;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop);

    let removeNative: (() => void) | undefined;
    if (isNativePlatform()) {
      const p = CapacitorApp.addListener("backButton", () => {
        window.history.back();
      });
      removeNative = () => { p.then((h) => h.remove()).catch(() => {}); };
    }

    return () => {
      window.removeEventListener("popstate", onPop);
      removeNative?.();
      if (!consumedSentinelRef.current && (window.history.state as any)?.__fullscreenViewer) {
        window.history.back();
      }
    };
  }, [open]);

  if (!images.length) return null;
  const currentImage = images[index] || images[0];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        hideCloseButton
        className="w-screen h-screen max-w-none max-h-none rounded-none p-0 gap-0 border-0 bg-black/95 flex flex-col"
      >
        {/* Close button */}
        <div className="absolute top-0 right-0 z-20 p-3 safe-area-inset-top">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close fullscreen"
            className="text-white hover:bg-white/10 rounded-full"
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        {/* Image area */}
        <div
          className="flex-1 min-h-0 flex items-center justify-center relative"
          onClick={onClose}
        >
          <img
            src={currentImage}
            alt={alt}
            className="max-w-full max-h-full object-contain select-none"
            onClick={(e) => e.stopPropagation()}
          />

          <Badge
            variant="secondary"
            className="absolute bottom-4 left-4 z-10 text-xs font-medium bg-black/60 text-white border-white/20 backdrop-blur-sm hover:bg-black/60"
          >
            <Sparkles className="mr-1 h-3 w-3" />
            AI-generated
          </Badge>

          {hasMultiple && (
            <>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 rounded-full h-11 w-11"
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex((i) => (i - 1 + images.length) % images.length);
                }}
              >
                <ChevronLeft className="h-7 w-7" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 rounded-full h-11 w-11"
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex((i) => (i + 1) % images.length);
                }}
              >
                <ChevronRight className="h-7 w-7" />
              </Button>
            </>
          )}
        </div>

        {/* Thumbnails */}
        {hasMultiple && (
          <div
            className="shrink-0 flex gap-2 p-3 overflow-x-auto justify-center bg-black/60"
            onClick={(e) => e.stopPropagation()}
          >
            {images.map((url, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`View variation ${i + 1}`}
                className={cn(
                  "shrink-0 rounded-md overflow-hidden border-2 transition-colors",
                  i === index ? "border-primary" : "border-transparent hover:border-white/40"
                )}
              >
                <img src={url} alt="" className="w-14 h-14 object-cover" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
